import { combineClasses } from "@/lib/utils";
import CheckIcon from "@/components/SVGComponents/CheckIcon";
import styles from "./PillToggleGroup.module.scss";

export interface PillOption {
  value: string;
  label: string;
}

export interface PillToggleGroupProps {
  options: PillOption[];
  // Multi-select — clicking a pill toggles it in/out of `value` rather
  // than replacing it. At least one stays selected (clicking the last
  // remaining active pill is a no-op) — an empty audience is never a
  // useful state here.
  value: string[];
  onChange: (value: string[]) => void;
  // Prefixes a checkmark on an active pill instead of only recoloring it
  // — the look CreateGathering's Room/Location pills don't use, but
  // TeamRoles' "Chapter Access" picker does (see InviteAdminModal).
  checkOnActive?: boolean;
  disabled?: boolean;
}

const PillToggleGroup = ({ options, value, onChange, checkOnActive = false, disabled = false }: PillToggleGroupProps) => {
  const toggle = (optionValue: string) => {
    if (value.includes(optionValue)) {
      if (value.length === 1) return;
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  return (
    <div className={styles.group}>
      {options.map((option) => {
        const isActive = value.includes(option.value);
        return (
          <button
            key={option.value}
            type="button"
            disabled={disabled}
            className={combineClasses(
              styles.pill,
              isActive && (checkOnActive ? styles.pillCheckActive : styles.pillActive),
            )}
            onClick={() => toggle(option.value)}
          >
            {checkOnActive && isActive && <CheckIcon width={10} height={10} className={styles.pillCheck} />}
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

export default PillToggleGroup;
